//! Log Helper Functions

import type { LogEntry, LogLevel, LogDomain } from "../entities";

/**
 * Formatiert einen Log-Eintrag für die Anzeige
 *
 * @param log
 *
 * @returns {string} Formatierter Log-String
 *
 */
export const formatLogEntry = (log: LogEntry): string => {
  const timestamp = new Date(log.timestamp).toLocaleTimeString();
  const levelIcon = getLogLevelIcon(log.level);
  const domainIcon = getLogDomainIcon(log.domain);

  return `[${timestamp}] ${levelIcon} ${domainIcon} ${log.domain.toUpperCase()} ${log.message}`;
};

/**
 * Gibt das Icon für ein Log-Level zurück
 *
 * @param level
 *
 * @returns {string} Icon-String für das Log-Level
 *
 */
export const getLogLevelIcon = (level: LogLevel): string => {
  const icons = {
    debug: "🐛",
    error: "❌",
    info: "ℹ️",
    warn: "⚠️",
  };
  return icons[level];
};

/**
 * Gibt das Icon für eine Log-Domain zurück
 *
 * @param domain
 *
 * @returns {string} Icon-String für die Log-Domain
 *
 */
export const getLogDomainIcon = (domain: LogDomain): string => {
  const icons: Record<string, string> = {
    admin: "⚙️",
    api: "🌐",
    auth: "🔐",
    core: "🔧",
    custom: "📦",
    database: "🗄️",
    health: "❤️",
    productivity: "⚡",
    settings: "⚙️",
    smarthome: "🏠",
    system: "🖥️",
    ui: "🖥️",
  };
  return icons[domain] ?? "📦";
};

/**
 * Gibt die Farbe für ein Log-Level zurück
 *
 * @param level
 *
 * @returns {string} Hex-Farbcode für das Log-Level
 *
 */
export const getLogLevelColor = (level: LogLevel): string => {
  const colors = {
    debug: "#6b7280", // gray-500
    error: "#ef4444", // red-500
    info: "#3b82f6", // blue-500
    warn: "#f59e0b", // amber-500
  };
  return colors[level];
};

/**
 * Gibt die Farbe für eine Log-Domain zurück
 *
 * @param domain
 *
 * @returns {string} Hex-Farbcode für die Log-Domain
 *
 */
export const getLogDomainColor = (domain: LogDomain): string => {
  const colors: Record<string, string> = {
    admin: "#6b7280", // gray-500
    api: "#8b5cf6", // violet-500
    auth: "#f59e0b", // amber-500
    core: "#84cc16", // lime-500
    custom: "#8b5cf6", // violet-500
    database: "#10b981", // emerald-500
    health: "#ef4444", // red-500
    productivity: "#3b82f6", // blue-500
    settings: "#6366f1", // indigo-500
    smarthome: "#f97316", // orange-500
    system: "#374151", // gray-700
    ui: "#06b6d4", // cyan-500
  };
  return colors[domain] ?? "#8b5cf6";
};

/**
 * Formatiert einen Zeitstempel für die Anzeige
 *
 * @param timestamp
 *
 * @returns {string} Formatierter Zeitstempel-String
 *
 */
export const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString("de-DE", {
    fractionalSecondDigits: 3,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * Formatiert eine Dauer in Millisekunden
 *
 * @param durationMs
 *
 * @returns {string} Formatierte Dauer-String
 *
 */
export const formatDuration = (durationMs: number): string => {
  if (durationMs < 1000) {
    return `${String(durationMs)}ms`;
  } else if (durationMs < 60_000) {
    return `${(durationMs / 1000).toFixed(1)}s`;
  } else {
    const minutes = Math.floor(durationMs / 60_000);
    const seconds = Math.floor((durationMs % 60_000) / 1000);
    return `${String(minutes)}m ${String(seconds)}s`;
  }
};

/**
 * Truncates a message to a maximum length
 *
 * @param message
 *
 * @param maxLength
 *
 * @returns {string} Truncated message string
 *
 */
export const truncateMessage = (message: string, maxLength = 100): string => {
  if (message.length <= maxLength) {
    return message;
  }
  return message.slice(0, Math.max(0, maxLength)) + "...";
};

/**
 * Escapes HTML characters in a string
 *
 * @param text
 *
 * @returns {string} Escaped HTML string
 *
 */
export const escapeHtml = (text: string): string => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Highlights search terms in text
 *
 * @param text
 *
 * @param searchTerm
 *
 * @returns {string} Text with highlighted search terms
 *
 */
export const highlightSearchTerm = (text: string, searchTerm: string): string => {
  if (!searchTerm) {
    return text;
  }

  const regex = new RegExp(`(${escapeRegex(searchTerm)})`, "gi");
  return text.replace(regex, "<mark>$1</mark>");
};

/**
 * Escapes regex special characters
 *
 * @param text
 *
 * @returns {string} Escaped regex string
 *
 */
const escapeRegex = (text: string): string => {
  return text.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
};

/**
 * Groups logs by domain
 *
 * @param logs
 *
 * @returns {Record<LogDomain, LogEntry[]>} Grouped logs by domain
 *
 */
export const groupLogsByDomain = (logs: LogEntry[]): Record<LogDomain, LogEntry[]> => {
  const groups: Partial<Record<LogDomain, LogEntry[]>> = {};
  for (const log of logs) {
    const domain = log.domain;
    const group = groups[domain];
    if (group) {
      group.push(log);
    } else {
      groups[domain] = [log];
    }
  }
  return groups as Record<LogDomain, LogEntry[]>;
};

/**
 * Groups logs by level
 *
 * @param logs
 *
 * @returns {Record<LogLevel, LogEntry[]>} Grouped logs by level
 *
 */
export const groupLogsByLevel = (logs: LogEntry[]): Record<LogLevel, LogEntry[]> => {
  const groups: Partial<Record<LogLevel, LogEntry[]>> = {};
  for (const log of logs) {
    const level = log.level;
    const group = groups[level];
    if (group) {
      group.push(log);
    } else {
      groups[level] = [log];
    }
  }
  return groups as Record<LogLevel, LogEntry[]>;
};

/**
 * Calculates log statistics
 *
 * @param logs
 *
 * @returns {object} Log statistics object with byDomain, byLevel, newest, oldest, total
 *
 */
export const calculateLogStats = (logs: LogEntry[]) => {
  const stats = {
    byDomain: {} as Record<LogDomain, number>,
    byLevel: {} as Record<LogLevel, number>,
    newest: null as Date | null,
    oldest: null as Date | null,
    total: logs.length,
  };

  for (const log of logs) {
    // Count by level
    const currentLevelCount = stats.byLevel[log.level];
    stats.byLevel[log.level] = (currentLevelCount || 0) + 1;

    // Count by domain
    const currentDomainCount = stats.byDomain[log.domain];
    stats.byDomain[log.domain] = (currentDomainCount || 0) + 1;

    // Track oldest and newest
    const timestamp = new Date(log.timestamp);
    if (!stats.oldest || timestamp < stats.oldest) {
      stats.oldest = timestamp;
    }
    if (!stats.newest || timestamp > stats.newest) {
      stats.newest = timestamp;
    }
  }

  return stats;
};
